import React, { useContext, useState } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Button,
  ActivityIndicator,
} from "react-native";
import { SessionContext } from "../../../../contexts";
import { NormalText } from "../../../common/native";
import {
  createDummyData,
  deleteDummyAppointments,
} from "../../../../services/api-clinic";
import { logUIError } from "../../../../services/api-misc";
import IntegrationSetupForm from "../integrations/IntegrationSetupForm";
import IntegrationManager from "../integrations/IntegrationManager";
import AutoGenerateBeforeAfterPanel from "./AutoGenerateBeforeAfterPanel";

const DebugPage: React.FC = () => {
  const session = useContext(SessionContext)?.session;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [`[${timestamp}] ${message}`, ...prev]);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const handleCreatePatients = async () => {
    if (isLoading || !session || !session.clinicId) return;

    try {
      setIsLoading(true);
      addLog("Creating dummy patients...");
      const response = await createDummyData(session, session.clinicId, {
        create_patients: true,
        create_appointments: false,
      });
      addLog(`✅ Success: ${response.message}`);
      addLog(`📊 Patients created: ${response.patients_created}`);
    } catch (error) {
      addLog(`❌ Error creating patients: ${error}`);
      console.error("Error creating dummy patients:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAppointments = async () => {
    if (isLoading || !session || !session.clinicId) return;

    try {
      setIsLoading(true);
      addLog("Creating dummy appointments...");
      const response = await createDummyData(session, session.clinicId, {
        create_patients: false,
        create_appointments: true,
      });
      addLog(`✅ Success: ${response.message}`);
      addLog(`📅 Appointments created: ${response.appointments_created}`);
    } catch (error) {
      addLog(`❌ Error creating appointments: ${error}`);
      console.error("Error creating dummy appointments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBoth = async () => {
    if (isLoading || !session || !session.clinicId) return;

    try {
      setIsLoading(true);
      addLog("Creating dummy patients and appointments...");
      const response = await createDummyData(session, session.clinicId, {
        create_patients: true,
        create_appointments: true,
      });
      addLog(`✅ Success: ${response.message}`);
      addLog(`👥 Patients created: ${response.patients_created}`);
      addLog(`📅 Appointments created: ${response.appointments_created}`);
    } catch (error) {
      addLog(`❌ Error creating data: ${error}`);
      console.error("Error creating dummy data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAppointments = async () => {
    if (isLoading || !session || !session.clinicId) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete all dummy appointments? This action cannot be undone.",
    );

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      addLog("Deleting dummy appointments...");
      const response = await deleteDummyAppointments(session, session.clinicId);
      addLog(`✅ Success: ${response.message}`);
      addLog(`🗑️ Appointments deleted: ${response.deleted_count}`);
    } catch (error) {
      addLog(`❌ Error deleting appointments: ${error}`);
      console.error("Error deleting dummy appointments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestLogUIError = async () => {
    if (!session || !session.clinicId) return;

    const correlationId = `debug-loguierror-${Date.now()}`;
    addLog(`Sending logUIError test (${correlationId})...`);

    try {
      await logUIError({
        message: "DebugPage manual logUIError test",
        page_url: typeof window !== "undefined" ? window.location.href : null,
        user_email: session.userEmail || null,
        clinic_id: session.clinicId || null,
        clinic_name: session.clinicName || null,
        context: {
          source: "DebugPage",
          test_type: "manual_log_ui_error",
          correlation_id: correlationId,
          env: process.env.EXPO_PUBLIC_ENV || "unknown",
          user_role: session.role,
          sent_at_iso: new Date().toISOString(),
        },
      });
      addLog(`✅ logUIError test sent (${correlationId})`);
    } catch (error) {
      addLog(`❌ logUIError test failed (${correlationId}): ${error}`);
      console.error("logUIError test failed:", error);
    }
  };

  if (!session) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
        <NormalText text="Loading..." style={styles.loadingText} />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.header}>
        <NormalText text="🐛 Debug Tools" style={styles.title} />
        <NormalText
          text="Development tools for testing and debugging"
          style={styles.subtitle}
        />
      </View>

      {/* Session Info */}
      <View style={styles.section}>
        <NormalText text="📋 Session Information" style={styles.sectionTitle} />
        <View style={styles.infoContainer}>
          <NormalText
            text={`User: ${session.userEmail}`}
            style={styles.infoText}
          />
          <NormalText
            text={`Clinic: ${session.clinicName}`}
            style={styles.infoText}
          />
          <NormalText
            text={`Clinic ID: ${session.clinicId}`}
            style={styles.infoText}
          />
          <NormalText text={`Role: ${session.role}`} style={styles.infoText} />
          <NormalText
            text={`Environment: ${process.env.EXPO_PUBLIC_ENV || "development"}`}
            style={styles.infoText}
          />
        </View>
      </View>

      {/* Dummy Data Controls */}
      <View style={styles.section}>
        <NormalText
          text="🎯 Dummy Data Management"
          style={styles.sectionTitle}
        />
        <View style={styles.buttonContainer}>
          <View style={styles.buttonRow}>
            <Button
              title="Create Patients"
              onPress={handleCreatePatients}
              color={tokens.color.primary}
              disabled={isLoading}
            />
            <Button
              title="Create Appointments"
              onPress={handleCreateAppointments}
              color={tokens.color.primary}
              disabled={isLoading}
            />
          </View>

          <View style={styles.buttonRow}>
            <Button
              title="Create Both"
              onPress={handleCreateBoth}
              color={tokens.color.success}
              disabled={isLoading}
            />
            <Button
              title="Delete Appointments"
              onPress={handleDeleteAppointments}
              color={tokens.color.danger}
              disabled={isLoading}
            />
          </View>

          {isLoading && (
            <View style={styles.loadingIndicator}>
              <ActivityIndicator size="small" color={tokens.color.primary} />
              <NormalText text="Processing..." style={styles.loadingText} />
            </View>
          )}
        </View>
      </View>

      <AutoGenerateBeforeAfterPanel session={session} onLog={addLog} />

      {/* Logs Section */}
      <View style={styles.section}>
        <View style={styles.logHeader}>
          <NormalText text="📝 Activity Logs" style={styles.sectionTitle} />
          <Button
            title="Clear"
            onPress={clearLogs}
            color={tokens.color.muted}
          />
        </View>
        <View style={styles.logsContainer}>
          {logs.length === 0 ? (
            <NormalText
              text="No activity yet..."
              style={styles.emptyLogsText}
            />
          ) : (
            logs.map((log, index) => (
              <NormalText key={index} text={log} style={styles.logText} />
            ))
          )}
        </View>
      </View>

      {/* Quick Links */}
      <View style={styles.section}>
        <NormalText text="🔗 Quick Actions" style={styles.sectionTitle} />
        <View style={styles.quickLinksContainer}>
          <Button
            title="Test logUIError"
            onPress={handleTestLogUIError}
            color={tokens.color.warning}
          />
          <Button
            title="Refresh Page"
            onPress={() => window.location.reload()}
            color={tokens.color.neutral}
          />
          <Button
            title="Open Console"
            onPress={() => {
              console.log("Debug info:", {
                session,
                environment: process.env.EXPO_PUBLIC_ENV,
              });
              addLog("Debug info logged to console");
            }}
            color={tokens.color.neutral}
          />
        </View>
      </View>

      {/* Integration Setup */}
      <View style={styles.section}>
        <IntegrationSetupForm
          session={session}
          onSuccess={() => addLog("✅ Integration created")}
        />
      </View>

      <View style={styles.section}>
        <IntegrationManager
          session={session}
          onChange={() => addLog("🔁 Integration updated or removed")}
        />
      </View>
    </ScrollView>
  );
};

const tokens = {
  color: {
    primary: "#00B7F4",
    success: "#28A745",
    danger: "#FF6B35",
    warning: "#E67E22",
    neutral: "#6C757D",
    muted: "#666",
    text: "#333",
    textSecondary: "#555",
    textTertiary: "#999",
    background: "#fff",
    surface: "#f8f9fa",
    border: "#e9ecef",
    borderStrong: "#ddd",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  radius: {
    sm: 8,
    md: 12,
  },
  font: {
    mono: "monospace",
  },
  fontWeight: {
    semibold: "600",
    bold: "700",
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 28,
  },
  lineHeight: {
    xs: 16,
  },
  borderWidth: {
    sm: 1,
  },
  size: {
    full: "100%",
    logsMaxHeight: 300,
  },
};

const styles = StyleSheet.create({
  container: {
    padding: tokens.spacing.xl,
    backgroundColor: tokens.color.background,
    minHeight: "100%" as any,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.color.background,
    minHeight: "100%" as any,
  },
  header: {
    marginBottom: tokens.spacing.xxl,
    alignItems: "center",
  },
  title: {
    fontSize: tokens.fontSize.xl,
    fontWeight: tokens.fontWeight.bold as "700",
    color: tokens.color.text,
    marginBottom: tokens.spacing.sm,
  },
  subtitle: {
    fontSize: tokens.fontSize.md,
    color: tokens.color.muted,
    textAlign: "center",
  },
  section: {
    marginBottom: tokens.spacing.xxl,
    padding: tokens.spacing.lg,
    backgroundColor: tokens.color.surface,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.borderWidth.sm,
    borderColor: tokens.color.border,
  },
  sectionTitle: {
    fontSize: tokens.fontSize.lg,
    fontWeight: tokens.fontWeight.semibold as "600",
    color: tokens.color.text,
    marginBottom: tokens.spacing.lg,
  },
  infoContainer: {
    gap: tokens.spacing.sm,
  },
  infoText: {
    fontSize: tokens.fontSize.sm,
    color: tokens.color.textSecondary,
    fontFamily: tokens.font.mono,
  },
  buttonContainer: {
    gap: tokens.spacing.lg,
  },
  buttonRow: {
    flexDirection: "row",
    gap: tokens.spacing.lg,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  loadingIndicator: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.lg,
  },
  loadingText: {
    color: tokens.color.muted,
    fontSize: tokens.fontSize.sm,
  },
  logHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacing.lg,
  },
  logsContainer: {
    maxHeight: tokens.size.logsMaxHeight,
    backgroundColor: tokens.color.background,
    borderRadius: tokens.radius.sm,
    padding: tokens.spacing.md,
    borderWidth: tokens.borderWidth.sm,
    borderColor: tokens.color.borderStrong,
  },
  emptyLogsText: {
    color: tokens.color.textTertiary,
    fontStyle: "italic",
    textAlign: "center",
  },
  logText: {
    fontSize: tokens.fontSize.xs,
    color: tokens.color.text,
    fontFamily: tokens.font.mono,
    marginBottom: tokens.spacing.xs,
    lineHeight: tokens.lineHeight.xs,
  },
  quickLinksContainer: {
    flexDirection: "row",
    gap: tokens.spacing.lg,
    justifyContent: "center",
    flexWrap: "wrap",
  },
});

export default DebugPage;
